// ─── Alignment Actions (Phase 1.8) ────────────────────────────
// Single-element alignment + z-order + duplicate helpers.
//
// All actions write BOTH legacy x/y/w AND new xPercent/yPercent/widthPercent
// so the section-v1 save adapter keeps everything in sync and the legacy
// renderer also stays correct.
//
// Distribute / equal-spacing is intentionally NOT here — deferred to Phase
// 1.9 alongside multi-select / photo-row conversion.

import { v4 as uuidv4 } from 'uuid';

const ANCHOR_FOR_ACTION = {
  'align-left':   'tl',
  'align-right':  'tr',
  'align-top':    't',
  'align-bottom': 'b',
  // Center actions keep the existing anchor's horizontal/vertical character.
  'center-horizontal': null,
  'center-vertical':   null,
};

function patchMobile(element, patch) {
  const mobile = (element.layout && element.layout.mobile) || {};
  const next = { ...mobile, ...patch };
  // Keep legacy x/y/w in sync when the new percent fields change.
  if (patch.xPercent != null) next.x = patch.xPercent / 100;
  if (patch.yPercent != null) next.y = patch.yPercent / 100;
  if (patch.widthPercent != null) next.w = patch.widthPercent / 100;
  return {
    ...element,
    layout: { ...(element.layout || {}), mobile: next },
  };
}

/**
 * Align an element relative to the section bounds.
 * @param {object} element
 * @param {'align-left'|'align-right'|'align-top'|'align-bottom'|'center-horizontal'|'center-vertical'} action
 */
export function alignElement(element, action) {
  if (!element || element.locked) return element;
  const mobile = (element.layout && element.layout.mobile) || {};
  const currentAnchor = mobile.anchor || 'c';

  switch (action) {
    case 'center-horizontal':
      // Switch anchor's horizontal part to center, keep vertical part.
      return patchMobile(element, {
        xPercent: 50,
        anchor: normalizeCenterAnchor(currentAnchor, 'h'),
      });
    case 'center-vertical':
      return patchMobile(element, {
        yPercent: 50,
        anchor: normalizeCenterAnchor(currentAnchor, 'v'),
      });
    case 'align-left':
      return patchMobile(element, { xPercent: 0, anchor: ANCHOR_FOR_ACTION[action] });
    case 'align-right':
      return patchMobile(element, { xPercent: 100, anchor: ANCHOR_FOR_ACTION[action] });
    case 'align-top':
      return patchMobile(element, { yPercent: 0, anchor: ANCHOR_FOR_ACTION[action] });
    case 'align-bottom':
      return patchMobile(element, { yPercent: 100, anchor: ANCHOR_FOR_ACTION[action] });
    default:
      return element;
  }
}

/**
 * Replace just the horizontal or vertical portion of an anchor with center.
 *   normalizeCenterAnchor('tl', 'h') → 't'   (top-center)
 *   normalizeCenterAnchor('tl', 'v') → 'l'   (center-left)
 *   normalizeCenterAnchor('c', *)    → 'c'
 */
function normalizeCenterAnchor(anchor, axis) {
  // Anchor codes: tl|t|tr|l|c|r|bl|b|br
  if (anchor === 'c') return 'c';
  if (axis === 'h') {
    // Strip horizontal: 'tl'->'t', 'tr'->'t', 'bl'->'b', 'br'->'b', 'l'->'c', 'r'->'c'
    if (anchor === 'l' || anchor === 'r') return 'c';
    if (anchor === 'tl' || anchor === 'tr') return 't';
    if (anchor === 'bl' || anchor === 'br') return 'b';
    return anchor;
  }
  // axis === 'v': strip vertical
  if (anchor === 't' || anchor === 'b') return 'c';
  if (anchor === 'tl' || anchor === 'bl') return 'l';
  if (anchor === 'tr' || anchor === 'br') return 'r';
  return anchor;
}

/** Move element forward in z (one step). Clamps to top. */
export function bringForward(element, allElements) {
  if (!element || element.locked) return element;
  const mobile = (element.layout && element.layout.mobile) || {};
  const currentZ = mobile.z || 1;
  const maxZ = Math.max(...allElements.map((e) => (e.layout?.mobile?.z) || 1));
  if (currentZ >= maxZ + 1) return element;
  return patchMobile(element, { z: Math.min(currentZ + 1, maxZ + 1) });
}

/** Move element backward in z (one step). Clamps to 0. */
export function sendBackward(element) {
  if (!element || element.locked) return element;
  const mobile = (element.layout && element.layout.mobile) || {};
  const currentZ = mobile.z || 1;
  if (currentZ <= 0) return element;
  return patchMobile(element, { z: Math.max(currentZ - 1, 0) });
}

/**
 * Clone an element with a new id and a small visible offset (+4% / +4%).
 * Returns the new element only — caller is responsible for adding it to the
 * section's _elements array.
 */
export function duplicateElement(element) {
  if (!element) return null;
  const mobile = (element.layout && element.layout.mobile) || {};
  const nx = clampPct((mobile.xPercent != null ? mobile.xPercent : (mobile.x || 0.5) * 100) + 4);
  const ny = clampPct((mobile.yPercent != null ? mobile.yPercent : (mobile.y || 0.5) * 100) + 4);
  return {
    ...element,
    id: `el_${uuidv4().slice(0, 8)}`,
    layout: {
      ...(element.layout || {}),
      mobile: {
        ...mobile,
        xPercent: nx, yPercent: ny,
        x: nx / 100, y: ny / 100,
      },
    },
  };
}

function clampPct(v) { return Math.max(0, Math.min(100, v)); }
